import { BaseSupabaseService } from './BaseSupabaseService';
import config from '../config/environment';
import supabase from '../config/supabase';
import { Anamnesis, SubmitAnamnesisPayload } from '../types';
import { handleServiceError } from '../utils/errorHandler';

class AnamnesisServiceClass extends BaseSupabaseService<Anamnesis> {
  constructor() {
    super('anamnesis');
  }

  async findByStudentId(studentId: number): Promise<Anamnesis | null> {
    const results = await this.findAllByStudentId(studentId, 1);
    return results[0] || null;
  }

  async findAllByStudentId(studentId: number, limit?: number): Promise<Anamnesis[]> {
    return this.findAll({
      filters: [{ column: 'student_id', value: studentId }],
      orderBy: { column: 'created_at', ascending: false },
      limit,
    });
  }

  async upsertForStudent(
    studentId: number,
    data: Partial<Anamnesis>,
    anamnesisId?: number | null,
  ): Promise<Anamnesis> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    const anamnesisPayload = anamnesisId
      ? { id: anamnesisId, ...data }
      : data;

    const response = await fetch(`${config.supabaseFunctionsUrl}/students`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.supabaseAnonKey,
      },
      body: JSON.stringify({
        action: 'update',
        payload: {
          id: studentId,
          anamnesis: anamnesisPayload,
        },
      }),
    });

    const body = await response.json().catch(() => ({}));

    if (!response.ok || body?.success === false) {
      handleServiceError(
        new Error(body?.error?.message || 'Erro ao salvar anamnese'),
        'salvar anamnese',
      );
    }

    if (anamnesisId) {
      const savedById = await this.findById(anamnesisId);
      if (!savedById) {
        handleServiceError(new Error('Anamnese não encontrada após salvar.'), 'salvar anamnese');
      }
      return savedById;
    }

    const saved = await this.findByStudentId(studentId);
    if (!saved) {
      handleServiceError(new Error('Anamnese não encontrada após salvar.'), 'salvar anamnese');
    }

    return saved;
  }

  async submitRegistration(payload: SubmitAnamnesisPayload): Promise<void> {
    if (!config.supabaseFunctionsUrl) {
      handleServiceError(new Error('URL das Edge Functions não configurada.'), 'enviar anamnese');
    }

    try {
      const response = await fetch(`${config.supabaseFunctionsUrl}/submit-anamnesis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: config.supabaseAnonKey,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        handleServiceError(new Error(errorPayload?.error || 'Erro ao enviar anamnese'), 'enviar anamnese');
      }
    } catch (error: unknown) {
      handleServiceError(error, 'enviar anamnese');
    }
  }
}

export const AnamnesisService = new AnamnesisServiceClass();
